import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Zap, Shield, Layers } from "lucide-react";
import { TOOLS, CATEGORIES } from "@/data/tools";
import ToolCard from "@/components/ToolCard";
import AdSlot from "@/components/AdSlot";
import SEO from "@/components/SEO";
import { useI18n } from "@/context/I18nContext";
import { HOME } from "@/constants/testIds";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const popular = TOOLS.filter((x) => x.featured).slice(0, 8);
  const latest = TOOLS.slice(0, 6);

  const FAQS = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4") },
    { q: t("faq.q5"), a: t("faq.a5") },
  ];

  const subscribe = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/newsletter`, { email }); toast.success(t("newsletter.success")); setEmail(""); }
    catch { toast.error(t("newsletter.error")); }
  };

  return (
    <div>
      <SEO
        title="ImageKitHub — Free, Private, Browser-Based Image Tools"
        description="23+ fast image tools that run in your browser. Compress, resize, convert, crop, and edit images without uploading anything."
        jsonLd={[
          { "@context": "https://schema.org", "@type": "WebSite", name: "ImageKitHub", url: window.location.origin, potentialAction: { "@type": "SearchAction", target: `${window.location.origin}/tools?q={search_term_string}`, "query-input": "required name=search_term_string" } },
          { "@context": "https://schema.org", "@type": "Organization", name: "ImageKitHub", url: window.location.origin },
        ]}
      />

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 text-xs font-medium">
              <Sparkles size={12} /> {t("hero.badge")}
            </span>
            <h1 data-testid={HOME.heroTitle} className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 dark:text-white leading-[1.05]">
              {t("hero.title_1")} <span className="text-blue-600 dark:text-blue-400">{t("hero.title_2")}</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400 max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link data-testid={HOME.ctaExplore} to="/tools" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors">
                {t("hero.cta_primary")} <ArrowRight size={16} />
              </Link>
              <Link to="/tools/image-compressor" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5 font-medium transition-colors">
                {t("hero.cta_secondary")}
              </Link>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {[{ icon: Zap, k: "24+", v: t("hero.stat_tools") }, { icon: Shield, k: "100%", v: t("hero.stat_private") }, { icon: Layers, k: "0MB", v: t("hero.stat_uploaded") }].map((s, i) => {
              const I = s.icon;
              return (
                <div key={i} className="p-5 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#121316] flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 grid place-items-center"><I size={18} /></div>
                  <div>
                    <div className="text-2xl font-semibold text-slate-900 dark:text-white">{s.k}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{s.v}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><AdSlot label="Sponsored" /></div>

      <Section title={t("section.popular.title")} subtitle={t("section.popular.subtitle")}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {popular.map((tt, i) => <ToolCard key={tt.slug} tool={tt} index={i} />)}
        </div>
      </Section>

      <Section title={t("section.categories.title")} subtitle={t("section.categories.subtitle")}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CATEGORIES.map((c) => {
            const count = TOOLS.filter((x) => x.category === c.id).length;
            return (
              <Link key={c.id} to={`/tools?category=${c.id}`} className="p-6 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#121316] hover:border-blue-500/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-300">
                <div className={`w-10 h-10 rounded-lg ${c.color}`} />
                <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">{c.name}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{count} {t("categories.tools_count")}</p>
              </Link>
            );
          })}
        </div>
      </Section>

      <Section title={t("section.latest.title")} subtitle={t("section.latest.subtitle")}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {latest.map((tt, i) => <ToolCard key={tt.slug} tool={tt} index={i} />)}
        </div>
      </Section>

      <Section title={t("section.faq.title")} subtitle={t("section.faq.subtitle")}>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible>
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-slate-200 dark:border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-slate-900 dark:text-white">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 dark:text-slate-400 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#121316] p-8 sm:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">{t("newsletter.title")}</h2>
              <p className="mt-3 text-slate-600 dark:text-slate-400">{t("newsletter.subtitle")}</p>
            </div>
            <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid={HOME.newsletterEmail}
                placeholder={t("newsletter.email_placeholder")}
                className="flex-1 rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-[#1a1c20] px-5 py-3 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-colors"
              />
              <button type="submit" data-testid={HOME.newsletterSubmit} className="px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors">
                {t("newsletter.subscribe")}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h2>
          {subtitle && <p className="mt-2 text-slate-500 dark:text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}
